/*
 * ASSETS da Whiz Farma.
 *
 * Nomenclatura do repositório, confirmada olhando `logo-light-whiz.png`:
 * "light" é a logo de TEXTO CLARO, para fundo escuro; "dark" é a de texto
 * escuro, para fundo claro. Não é o tema em que ela aparece — é o contrário.
 * Os arquivos da Farma seguem a mesma regra.
 *
 *   ✅ logo.light   logo-light-Farma.png — texto verde-claro.
 *                   Aparece no painel escuro das telas de autenticação
 *                   (login do admin e do cliente, 2FA, esqueci a senha,
 *                   redefinir senha, conta congelada). É a mais visível.
 *
 *   ✅ logo.dark    logo-dark-Farma.png — texto verde. Vai no bloco mobile
 *                   dessas mesmas telas, onde o fundo é branco.
 *
 *   ✅ iconLight    icon-light-whiz-farma.png — o "!" da marca, tema escuro
 *   ✅ iconDark     icon-dark-whiz-farma.png  — o "!" da marca, tema claro
 *                   Os dois vão no topo da barra lateral, que limita a altura
 *                   a 30px sem forçar a largura: o ícone em pé (~1:3,5) entra
 *                   sem distorcer.
 *
 *   ⚠️ logo.svg     a marca não tem versão vetorial; usa o `iconDark`. O campo
 *                   só vira `<img src>` (modal de chat com IA), então PNG
 *                   serve. Lá a imagem tem `border-radius: 50%`, que corta as
 *                   pontas do "!".
 *
 *   ⚠️ favicon e pwaIcons saem do `iconLight`, que NÃO é quadrado. Favicon e
 *                   ícone do app instalado precisam ser quadrados para ficar
 *                   legíveis. Quando o design entregar a versão quadrada do
 *                   ícone, é apontar `favicon`, `pwaIcons` e `logo.svg` para ela.
 */
import logoLightWhizFarma from '@/assets/images/logo-light-Farma.png';
import logoDarkWhizFarma from '@/assets/images/logo-dark-Farma.png';
import iconeLightWhizFarma from '@/assets/images/icon-light-whiz-farma.png';
import iconeDarkWhizFarma from '@/assets/images/icon-dark-whiz-farma.png';
import type { BrandingConfig } from './types';

/**
 * Whiz Farma — ambiente próprio, irmão da ProsaZap.
 *
 * Compartilha a PALETA da ProsaZap (decisão de produto), e é por isso que o
 * `branding.css` trata as duas no mesmo bloco de tokens em vez de repetir ~45
 * linhas de cor. O que distingue as marcas aqui é nome, títulos e logo.
 *
 * As ilustrações de `images` também são as da ProsaZap, menos as do chat vazio
 * (`emptyChat` e `emptyChatTeam`): essas desenham a logo da marca na tela do
 * celular, então a Farma tem as suas. As demais (papel de parede do chat,
 * avatar padrão, GIF da janela de Novidades vazia) são desenhos tingidos pela paleta, não a
 * marca em si — com a mesma paleta, servem às duas.
 */
export const whizFarmaBranding: BrandingConfig = {
  key: 'WHIZ_FARMA',
  companyName: 'Whiz Farma',
  shortName: 'Whiz Farma',
  titles: {
    admin: 'Whiz Farma - Ambiente Administrativo',
    user: 'Whiz Farma - Gestão de Atendimento',
  },
  logo: {
    light: logoLightWhizFarma,
    dark: logoDarkWhizFarma,
    svg: iconeDarkWhizFarma,
    iconLight: iconeLightWhizFarma,
    iconDark: iconeDarkWhizFarma,
  },
  favicon: iconeLightWhizFarma,
  themeColor: '#198653',
  pwaIcons: [
    // Um só ícone com `sizes: "any"`: a marca ainda não tem o jogo de tamanhos
    // que a Whiz tem em `public/img/icons`. O navegador reescala.
    { src: iconeLightWhizFarma, sizes: 'any', type: 'image/png', purpose: 'any maskable' },
  ],
  colors: {
    light: {
      primary: '#198653',
      primaryRgb: '25, 134, 83',
      success: '#2bab60',
      successHover: '#198653',
      loaderPrimary: '#198653',
      loaderSecondary: '#2bab60',
    },
    dark: {
      primary: '#1ea465',
      primaryRgb: '30, 164, 101',
      success: '#39c668',
      successHover: '#2bab60',
      loaderPrimary: '#1ea465',
      loaderSecondary: '#39c668',
    },
  },
  gradients: {
    primary: 'linear-gradient(135deg, #198653, #2bab60)',
    chatBubbleSent: {
      light: 'linear-gradient(90deg, #2bab609b, #198653)',
      dark: 'linear-gradient(90deg, #1ea465, #2bab60bb)',
    },
  },
  loginAssets: {
    backgroundImage: '',
    overlayGradient: 'linear-gradient(45deg, rgba(25, 134, 83, 0.85), rgba(43, 171, 96, 0.78))',
  },
  images: {
    emptyChat: '/img/celular_farma.png',
    emptyChatTeam: '/img/celular_team_farma.png',
    chatWallpaper: '/img/fundo_prosa.png',
    avatarFallback: '/img/empty_prosa.png',
    avatarFallbackGroup: '/img/empty_group_prosa.png',
    communicationEmpty: '/img/comunicacao_empty_prosa.gif',
  },
  texts: {
    loginFooterAdmin: '© {year} Whiz Farma Admin. Acesso monitorado.',
    loginFooterUser: '© {year} Whiz Farma. Todos os direitos reservados.',
    installAppButton: 'Instalar Aplicativo Whiz Farma',
    loadingApp: 'Carregando Whiz Farma...',
    welcomeChatTitle: 'Seja bem-vindo ao Whiz Farma',
    welcomeChatText: 'O Whiz Farma ajuda você a organizar, acompanhar e gerenciar seus atendimentos em um só lugar.',
  },
};
